import React from 'react'
import PropTypes from 'prop-types'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import dynamic from 'next/dynamic'

import Error from '@shared/components/error'
import Layout from '@shared/components/layout'
import { pageHeaderLoader, pageLoading } from '@shared/libs/allLoader'
import ArticleSkeleton from '@shared/components/skeleton/components/articleSkeleton'

const CurrentSeries = dynamic(() => import('@shared/components/widgets/currentSeries'), { loading: () => pageLoading() })
const PageHeader = dynamic(() => import('@shared-components/pageHeader'), { loading: () => pageHeaderLoader() })
const CricketPlayerContent = dynamic(() => import('@shared/components/player/playerFilters'), { loading: () => pageHeaderLoader() })
const NoData = dynamic(() => import('@shared/components/noData'))
const SearchCard = dynamic(() => import('@shared/components/searchComponents/searchCard'), { loading: () => <ArticleSkeleton type="t" /> })

// export const config = { amp: 'hybrid' }
// const CategoryMain = dynamic(() => import('@shared/components/categoryMain'), { loading: () => pageLoading() })

function CricketPlayer({ seoData, players }) {
  return (
    <Layout data={{ oSeo: { ...seoData, eType: 'st' } }}>
      <main className="my-3 my-md-4">
        <Container>
          <Row>
            <Col md={9}>
              <PageHeader
                name="Cricket Players"
                seoData={seoData}
              />
              <CricketPlayerContent players={players}>
                {({ list, showLoader }) => (
                  <Row className="gx-3">
                    {list?.map((p) => (
                      <Col key={p?._id} id={p?._id} sm={6} xxl={4}>
                        <SearchCard data={p} showPlayerCountry />
                      </Col>
                    ))}
                    {list?.length === 0 && (
                      <Col> <NoData /> </Col>
                    )}
                    {showLoader && (
                      <Col xs={12} className='text-center'><Spinner animation="border" size='md' /></Col>
                    )}
                  </Row>
                )}
              </CricketPlayerContent>
            </Col>
            <Col md={3} className='d-none d-md-block'>
              <CurrentSeries />
            </Col>
          </Row>
        </Container>
      </main>
    </Layout>
  )
}

CricketPlayer.propTypes = {
  seoData: PropTypes.object,
  players: PropTypes.array
}

export default Error(CricketPlayer)

export async function getServerSideProps({ req, res, params, query, resolvedUrl }) {
  const [graphql, articleQuery, utils, playerQuery] = await Promise.all([
    import('@shared-components/queryGraphql'),
    import('@graphql/article/article.query'),
    import('@shared/utils'),
    import('@graphql/players/players.query')
  ])

  // Check amp exists in query params
  const { hasAmp, redirectionRules } = utils.hasAmpInQueryParams(req?.url)
  if (hasAmp) return redirectionRules

  try {
    const [seoData, players] = await Promise.allSettled([
      graphql.default(articleQuery.GET_ARTICLE_ID, { input: { sSlug: 'cricket-players' } }),
      graphql.default(playerQuery.LIST_GLOBAL_PLAYERS, {
        input: {
          ePlayingRole: query?.['ct-ePlayingRole'] || null,
          eSex: query?.['ct-eSex'] || 'male',
          nLimit: 20,
          nSkip: 1,
          sAlphaSearch: query?.['ct-sAlphaSearch'] || null,
          sCountryFull: query?.['ct-sCountryFull'] || null,
          sSearch: query?.['ct-sSearch'] || null
        }
      })
    ])
    const seo = seoData?.value?.data?.getSeoData

    // Check Redirection
    const { redirectStatus, eCode, returnObj, props } = utils.checkRedirectionStatus(seo, query?.amp)
    if (redirectStatus && props) {
      res.statusCode = eCode
      return { props }
    } else if (redirectStatus) return returnObj

    res.setHeader('Cache-Control', 'public, max-age=86400')

    return {
      props: {
        seoData: seo,
        players: players?.value?.data?.listGlobalPlayers || []
      }
    }
  } catch (e) {
    // console.log({ slug: e })
    res.setHeader('Cache-Control', 'no-cache')
    const status = utils.handleApiError(e, resolvedUrl)
    return status
  }
}
